<!--
  - SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<div>
		<ul class="aliases-list">
			<!-- Primary alias -->
			<li>
				<AliasForm
					:account="account"
					:alias="accountAlias"
					:enable-update="false"
					:enable-delete="false">
					<NcButton
						v-if="!account.provisioningId"
						variant="tertiary-no-background"
						:aria-label="t('mail', 'Go back')"
						:name="t('mail', 'Change name')"
						@click="$emit('rename-primary-alias')">
						<template #icon>
							<IconRename :size="20" />
						</template>
					</NcButton>
				</AliasForm>
			</li>

			<!-- Secondary aliases -->
			<li v-for="alias in aliases" :key="alias.id">
				<AliasForm
					:account="account"
					:alias="alias"
					:on-update-alias="updateAlias"
					:on-delete="deleteAlias" />
			</li>

			<li v-if="showForm">
				<form id="createAliasForm" @submit.prevent="createAlias">
					<input
						v-model="newName"
						type="text"
						:placeholder="t('mail', 'Name')"
						required>
					<input
						v-model="newAlias"
						type="email"
						:placeholder="t('mail', 'Email address')"
						required>
				</form>
			</li>
		</ul>

		<div v-if="!account.provisioningId" class="mail-button-row">
			<NcButton
				v-if="!showForm"
				variant="primary"
				:aria-label="t('mail', 'Add alias')"
				@click="showForm = true">
				{{ t('mail', 'Add alias') }}
			</NcButton>

			<NcButton
				v-if="showForm"
				type="submit"
				variant="primary"
				form="createAliasForm"
				:aria-label="t('mail', 'Create alias')"
				:disabled="loading">
				<template #icon>
					<NcLoadingIcon v-if="loading" :size="20" />
					<IconCheck v-else :size="20" />
				</template>
				{{ t('mail', 'Create alias') }}
			</NcButton>
			<NcButton
				v-if="showForm"
				variant="tertiary-no-background"
				:aria-label="t('mail', 'Cancel')"
				@click="resetCreate">
				{{ t("mail", "Cancel") }}
			</NcButton>
		</div>
	</div>
</template>

<script>
import { NcButton, NcLoadingIcon } from '@nextcloud/vue'
import { mapStores } from 'pinia'
import IconCheck from 'vue-material-design-icons/Check.vue'
import IconRename from 'vue-material-design-icons/PencilOutline.vue'
import AliasForm from './AliasForm.vue'
import logger from '../logger.js'
import useMainStore from '../store/mainStore.js'

export default {
	name: 'AliasSettings',
	components: {
		AliasForm,
		NcButton,
		NcLoadingIcon,
		IconCheck,
		IconRename,
	},

	props: {
		account: {
			type: Object,
			required: true,
		},
	},

	data() {
		return {
			newAlias: '',
			newName: this.account.name,
			showForm: false,
			loading: false,
		}
	},

	computed: {
		...mapStores(useMainStore),
		aliases() {
			return this.account.aliases
		},

		accountAlias() {
			return {
				alias: this.account.emailAddress,
				name: this.account.name,
				provisioned: !!this.account.provisioningId,
				smimeCertificateId: this.account.smimeCertificateId,
			}
		},
	},

	methods: {
		async createAlias() {
			this.loading = true

			await this.mainStore.createAlias({
				account: this.account,
				alias: this.newAlias,
				name: this.newName,
			})

			logger.debug('created alias', {
				accountId: this.account.id,
				alias: this.newAlias,
				name: this.newName,
			})

			this.resetCreate()
			this.loading = false
		},

		resetCreate() {
			this.newAlias = ''
			this.newName = this.account.name
			this.showForm = false
		},

		async updateAlias(aliasId, newAlias) {
			const alias = this.aliases.find((alias) => alias.id === aliasId)
			await this.mainStore.updateAlias({
				account: this.account,
				aliasId: alias.id,
				alias: newAlias.alias,
				name: newAlias.name,
				smimeCertificateId: alias.smimeCertificateId,
			})
		},

		async deleteAlias(aliasId) {
			await this.mainStore.deleteAlias({
				account: this.account,
				aliasId,
			})
		},
	},
}
</script>

<style lang="scss" scoped>
input {
	width: 195px;
}
</style>
